import useSWR, { type SWRConfiguration } from 'swr';
import denApi from '@/lib/api/denApi';
import type { PendingInvitedMember } from '@mls-wallet/shared-types/member';
import { useMyOrganization } from '@/hooks/organization/use-my-organization';

// The server owns the member lifecycle shape, but JSON responses serialize
// Date fields as strings for the web client.
export type PendingInvite = Omit<
  PendingInvitedMember,
  'staleAt' | 'createdAt'
> & {
  staleAt: string;
  createdAt: string;
};

const fetcher = async (url: string) =>
  await denApi.get<PendingInvite[], void>(url);

export function usePendingInvites(
  organizationId: string | null,
  swrOptions?: SWRConfiguration<PendingInvite[]>
) {
  const { organization } = useMyOrganization();
  // Org context comes from x-current-org-id cookie (not URL params)
  // Pause requests until admin access to the current organization is confirmed.
  const url =
    organizationId &&
    organization?.id === organizationId &&
    organization.isAdmin
      ? `admin/organization/invitations/pending`
      : null;
  const { data, error, isLoading, mutate } = useSWR<PendingInvite[]>(
    url,
    fetcher,
    {
      revalidateOnFocus: false,
      refreshInterval: 10000, // Poll every 10 seconds for updates
      ...(swrOptions ?? {}),
    }
  );

  return {
    invites: data ?? [],
    error,
    isLoading,
    refresh: mutate,
  };
}
